import { useState, useEffect } from "react";
import { useAppStore } from "../store/useAppStore";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { LeadStatus } from "../types";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Link } from "react-router-dom";

const PIPELINE_COLUMNS: LeadStatus[] = [
  "New Lead",
  "Contacted",
  "Follow-Up",
  "Appointment Set",
  "Offer Made",
  "Under Contract"
];

export default function Pipeline() {
  const { leads, updateLead } = useAppStore();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const onDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const newStatus = destination.droppableId as LeadStatus;
    updateLead(draggableId, { status: newStatus });
  };

  if (!isMounted) return null; // Avoid hydration mismatch on DND

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 pb-8 min-h-[calc(100dvh-80px)]">
      <div className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm shadow-slate-900/5">
        <div className="grid gap-6 px-5 py-6 sm:px-8 lg:grid-cols-[1.1fr_.9fr] lg:items-end lg:px-10 lg:py-8">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">
              <span>Workflow</span>
              <span className="h-1 w-1 rounded-full bg-slate-300" />
              <span>Deal pipeline</span>
            </div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">Deal Pipeline</h1>
            <p className="max-w-2xl text-sm leading-6 text-slate-500">Drag and drop leads through a clearer, calmer workflow so every deal feels easier to manage.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">Columns</p>
              <p className="mt-1 text-2xl font-semibold text-slate-950">{PIPELINE_COLUMNS.length}</p>
            </div>
            <div className="rounded-2xl border border-blue-100 bg-blue-50/70 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-500">Active leads</p>
              <p className="mt-1 text-2xl font-semibold text-blue-700">{leads.length}</p>
            </div>
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-500">Current stage</p>
              <p className="mt-1 text-sm font-medium text-emerald-700">Keep the next touch obvious</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:hidden">
        {PIPELINE_COLUMNS.map((status) => {
          const columnLeads = leads.filter((l) => l.status === status);

          return (
            <Card key={status} className="border-slate-200 bg-white shadow-sm">
              <CardHeader className="p-4 pb-3 flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-sm font-semibold">{status}</CardTitle>
                <Badge variant="secondary" className="rounded-full">{columnLeads.length}</Badge>
              </CardHeader>
              <CardContent className="p-4 pt-0 space-y-3">
                {columnLeads.length > 0 ? (
                  columnLeads.map((lead) => (
                    <div key={lead.id} className="rounded-lg border border-slate-200 p-3 space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 truncate">{lead.sellerName}</p>
                          <p className="text-xs text-slate-500 truncate">{lead.propertyAddress}</p>
                        </div>
                        <Badge variant="outline" className="text-[10px] whitespace-nowrap">{lead.askingPrice ? `$${lead.askingPrice.toLocaleString()}` : "No price"}</Badge>
                      </div>
                      <Link to={`/leads/${lead.id}`} className="inline-flex text-sm font-medium text-primary hover:underline">
                        Open lead
                      </Link>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">No leads in this stage.</p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="hidden md:block flex-1 overflow-x-auto pb-4">
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="flex gap-4 min-h-full items-start">
            {PIPELINE_COLUMNS.map((status) => {
              const columnLeads = leads.filter((l) => l.status === status);

              return (
                <div key={status} className="flex-shrink-0 w-80 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50/80 flex flex-col max-h-full shadow-sm">
                  <div className="flex items-center justify-between border-b border-slate-200/80 bg-white/90 p-4 rounded-t-2xl">
                    <h3 className="font-semibold text-sm text-slate-900">{status}</h3>
                    <Badge variant="secondary" className="rounded-full bg-slate-100 text-slate-700">{columnLeads.length}</Badge>
                  </div>

                  <Droppable droppableId={status}>
                    {(provided, snapshot) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className={`flex-1 p-3 overflow-y-auto min-h-[150px] space-y-3 ${
                          snapshot.isDraggingOver ? "bg-muted/60" : ""
                        }`}
                      >
                        {columnLeads.map((lead, index) => (
                          <Draggable key={lead.id} draggableId={lead.id} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={{
                                  ...provided.draggableProps.style,
                                }}
                              >
                                <Card className={`cursor-grab hover:border-primary/50 transition-colors ${snapshot.isDragging ? "shadow-lg rotate-2" : "shadow-sm"}`}>
                                  <CardHeader className="p-3 pb-2">
                                    <CardTitle className="text-sm font-semibold flex justify-between">
                                      {lead.sellerName}
                                    </CardTitle>
                                  </CardHeader>
                                  <CardContent className="p-3 pt-0">
                                    <p className="text-xs text-muted-foreground truncate mb-2">{lead.propertyAddress}</p>
                                    <div className="flex justify-between items-center text-xs">
                                      <span className="font-medium text-emerald-600 dark:text-emerald-400">
                                        {lead.askingPrice ? `$${lead.askingPrice.toLocaleString()}` : 'No price'}
                                      </span>
                                      <Link to={`/leads/${lead.id}`} className="text-primary hover:underline">View</Link>
                                    </div>
                                  </CardContent>
                                </Card>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              );
            })}
          </div>
        </DragDropContext>
      </div>
    </div>
  );
}
